import { createSignal } from 'solid-js';
import { pushEvent } from '~/lib/event-log';

export interface VisibilityState {
  /** Current document visibility state. */
  state: () => 'visible' | 'hidden';
  /** Count of `visibilitychange` events since the page loaded. */
  changes: () => number;
  /** Cumulative ms the document has been visible since page load. */
  visibleMs: () => number;
  /** Cumulative ms the document has been hidden since page load. */
  hiddenMs: () => number;
  /** Timestamp (epoch ms) of the most recent state change. */
  lastChangeAt: () => number | null;
  /** Timestamp (epoch ms) of when tracking started (page load). */
  startedAt: () => number;
}

/** Reactive document visibility tracker.
 *
 *  Module-level singleton, mirroring `wake-lock.ts` and the geolocation
 *  tracker. The `visibilitychange` listener attaches once at module load
 *  and the counters accumulate for the entire page lifetime; they reset
 *  only on a full page reload. This is deliberate: switching tabs inside
 *  the SPA (a `<Show>` remount in App.tsx) is not a document visibility
 *  change and must not zero the session metrics. A previous factory-per-
 *  mount shape reset them on every DIAG remount; see prompt 09.
 *
 *  Tab survival during long foreground sessions is the largest open
 *  architectural question on iOS Safari. The cumulative hidden/visible
 *  counters here are what we read after a 30-60 minute walk to decide
 *  whether the PWA model is viable or Capacitor becomes mandatory. */

const startedAt = Date.now();

const [state, setState] = createSignal<'visible' | 'hidden'>(
  typeof document !== 'undefined' && document.visibilityState === 'hidden' ? 'hidden' : 'visible',
);
const [changes, setChanges] = createSignal(0);
const [visibleMs, setVisibleMs] = createSignal(0);
const [hiddenMs, setHiddenMs] = createSignal(0);
const [lastChangeAt, setLastChangeAt] = createSignal<number | null>(null);

// Moment we entered the current visibility segment. Elapsed time is
// attributed to visible/hidden on each tick and on each change so the
// DIAG readouts stay live between visibility transitions.
let segmentStartedAt = startedAt;

function flushSegment(): void {
  const now = Date.now();
  const delta = now - segmentStartedAt;
  segmentStartedAt = now;
  // Reads `state()` (the segment that is ending) before any setState.
  if (state() === 'visible') setVisibleMs((v) => v + delta);
  else setHiddenMs((v) => v + delta);
}

if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    flushSegment();
    setLastChangeAt(Date.now());
    setChanges((c) => c + 1);
    const next = document.visibilityState === 'hidden' ? 'hidden' : 'visible';
    setState(next);
    pushEvent('visibility_change', { state: next });
  });
  // Tick once per second so the current segment's accumulating duration
  // reads as live in the DIAG readouts even with no visibility change.
  setInterval(flushSegment, 1000);
}

/** Module-level document visibility tracker. State accumulates for the
 *  life of the page and persists across SPA tab switches; it resets only
 *  on a full page reload. */
export const visibility: VisibilityState = {
  state,
  changes,
  visibleMs,
  hiddenMs,
  lastChangeAt,
  startedAt: () => startedAt,
};
